/**
 * PlanningOrchestrationService
 *
 * This service orchestrates the planning workflow, extracting the core planning logic
 * from UI components. It serves as the foundation for:
 * 
 * 1. Decoupling planning logic from UI components
 * 2. Enabling optimistic UI updates (future enhancement)
 * 3. Centralizing planning workflow for better testability
 * 4. Supporting multi-user coordination via SignalR
 *
 * Planning Workflow:
 * 1. Validate inputs (date compatibility, driver availability)
 * 2. Pre-plan: Create/update TO with PBs → BC: FctAddPBToTO
 * 3. Update PBs: Container status and location
 * 4. Group: Combine related PBs
 * 5. Send: Finalize planning → BC: FctUpdateInfoOnTO
 *
 * Future: This service will be enhanced to support optimistic UI by:
 * - Creating optimistic state immediately
 * - Processing BC calls asynchronously
 * - Handling confirmations/failures via SignalR
 */

import { Injectable } from '@angular/core';
import { Observable, of, forkJoin, throwError, Subject } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import * as moment from 'moment';
import { NgRedux } from '@angular-redux/store';

import { SharedService } from './shared.service';
import { SharedActions } from '../shared.actions';
import { UserFeedbackService } from '../../services/user-feedback.service';
import { DevLoggerService } from './dev-logger.service';
import { IAppState } from '../../store/IAppState';
import { ActionKeys } from '../../pb-screen/action.keys';

import { PlanningBlock } from '../../pb-screen/models/planning-block/planning-block.interface';
import { TransportOrder } from '../models/transport-order/transport-order.interface';
import { TransportOrderPlanUnit } from '../models/transport-order/transport-order-plan-unit.interface';
import { TransportOrderWithPlanUnit } from '../models/transport-order/transport-order-with-plan-unit.interface';
import { CreateTransportOrder } from '../models/transport-order/create-transport-order.interface';
import { CapacitySlot } from '../models/capacity-slot.interface';
import { Driver } from '../../static/models/driver.interface';
import { UpdateContainerStatusModel } from '../models/update-container-status-model.interface';
import { AddressLocation } from '../models/address-location';
import {
  OptimisticPlanningBlock,
  OptimisticTransportOrder,
  OptimisticPlanResponse,
  PlanningStartedPayload,
  PlanningConfirmedPayload,
  PlanningFailedPayload,
  generateOptimisticId,
  generateCorrelationId
} from '../models/optimistic';

/**
 * Input for planning operation
 */
export interface PlanningInput {
  /** Planning blocks from depot-in selection */
  depotInPlanningBlocks: PlanningBlock[];
  /** Planning blocks from depot-out selection */
  depotOutPlanningBlocks: PlanningBlock[];
  /** Selected capacity slot (truck/driver/timeslot) */
  capacitySlot: CapacitySlot;
  /** Planning date */
  planDate: Date;
  /** User ID performing the action */
  userId: string;
  /** Optional driver override */
  selectedDriver?: Driver;
  /** Optional: existing TO to plan to */
  existingTransportOrder?: TransportOrder;
  /** Optional: update container status */
  statusUpdate?: {
    enabled: boolean;
    status?: string;
    location?: AddressLocation;
  };
}

/**
 * Result of planning operation
 */
export interface PlanningResult {
  success: boolean;
  transportOrderId?: string;
  transportOrder?: TransportOrder;
  planningBlocks?: PlanningBlock[];
  planUnit?: TransportOrderPlanUnit;
  error?: string;
}

/**
 * Date validation result
 */
export interface DateValidationResult {
  isValid: boolean;
  errors: string[];
  invalidBlocks: PlanningBlock[];
}

@Injectable({
  providedIn: 'root'
})
export class PlanningOrchestrationService {
  constructor(
    private readonly sharedService: SharedService,
    private readonly sharedActions: SharedActions,
    private readonly userFeedbackService: UserFeedbackService,
    private readonly devLogger: DevLoggerService,
    private readonly store: NgRedux<IAppState>
  ) {}

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN WORKFLOW METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Execute the full planning workflow
   * 
   * @param input Planning input parameters
   * @param options Additional options
   * @returns Observable of planning result
   */
  public executePlanning(
    input: PlanningInput,
    options: {
      skipDateValidation?: boolean;
      skipDriverAbsenceCheck?: boolean;
      groupAfterPlan?: boolean;
      sendAfterGroup?: boolean;
    } = { groupAfterPlan: true, sendAfterGroup: true }
  ): Observable<PlanningResult> {
    this.devLogger.planning('executePlanning started', {
      depotIn: input.depotInPlanningBlocks.length,
      depotOut: input.depotOutPlanningBlocks.length,
      planDate: input.planDate,
      hasExistingTO: !!input.existingTransportOrder
    });

    // Step 1: Validate dates (if not skipped)
    if (!options.skipDateValidation) {
      const dateValidation = this.validatePlanningDates(input);
      if (!dateValidation.isValid) {
        this.devLogger.planning('Date validation failed', dateValidation.errors);
        return of({
          success: false,
          error: dateValidation.errors.join('\n')
        });
      }
    }

    // Step 2: Choose workflow based on whether planning to existing TO
    if (input.existingTransportOrder) {
      return this.planToExistingTO(input, options);
    } else {
      return this.planToNewTO(input, options);
    }
  }

  /**
   * Plan to a new Transport Order
   */
  private planToNewTO(
    input: PlanningInput,
    options: { groupAfterPlan?: boolean; sendAfterGroup?: boolean }
  ): Observable<PlanningResult> {
    const planUnit: TransportOrderPlanUnit = {
      depotInPlanningBlocks: input.depotInPlanningBlocks,
      depotOutPlanningBlocks: input.depotOutPlanningBlocks,
    } as TransportOrderPlanUnit;

    const apiFormattedDate = moment(input.planDate).format('YYYY-MM-DD');

    this.devLogger.planning('Executing pre-plan action', { planDate: apiFormattedDate });

    return this.sharedService.prePlanAction(
      input.capacitySlot,
      planUnit,
      apiFormattedDate,
      input.userId,
      input.selectedDriver
    ).pipe(
      switchMap((prePlannedData: TransportOrderWithPlanUnit) => {
        this.devLogger.planning('Pre-plan succeeded', { toId: prePlannedData.transportOrder?.id });

        // Combine all planning blocks for updates
        const allPlanningBlocks = [
          ...prePlannedData.planningBlocks.depotInPlanningBlocks,
          ...prePlannedData.planningBlocks.depotOutPlanningBlocks
        ];

        // Update planning blocks (container status/location) if needed
        return this.updatePlanningBlocksIfNeeded(allPlanningBlocks, input).pipe(
          switchMap(() => {
            // Update display
            this.sharedActions.updatePlanningBlocksDisplayCpl(allPlanningBlocks);

            // Continue with group and send if requested
            if (options.groupAfterPlan) {
              return this.groupAndSend(prePlannedData, options.sendAfterGroup);
            }

            return of({
              success: true,
              transportOrderId: prePlannedData.transportOrder?.id,
              transportOrder: prePlannedData.transportOrder,
              planningBlocks: allPlanningBlocks,
              planUnit: prePlannedData.planningBlocks
            });
          })
        );
      }),
      catchError((error) => {
        this.devLogger.error('PlanningOrchestration', 'Pre-plan failed', error);
        // Attempt rollback
        this.sharedService.undoPreplan(planUnit, input.userId);
        return of({
          success: false,
          error: this.extractErrorMessage(error)
        });
      })
    );
  }

  /**
   * Plan to an existing Transport Order
   */
  private planToExistingTO(
    input: PlanningInput,
    options: { groupAfterPlan?: boolean; sendAfterGroup?: boolean }
  ): Observable<PlanningResult> {
    const apiFormattedDate = moment(input.planDate).format('YYYY-MM-DD');
    const allPbs = [...input.depotInPlanningBlocks, ...input.depotOutPlanningBlocks];

    this.devLogger.planning('Planning to existing TO', { 
      toId: input.existingTransportOrder.id, 
      pbCount: allPbs.length 
    });

    return this.sharedService.planToExistingTos(
      input.existingTransportOrder.id,
      allPbs,
      input.userId,
      true, // confirmDateChange
      apiFormattedDate
    ).pipe(
      switchMap((prePlannedData: TransportOrderWithPlanUnit) => {
        this.devLogger.planning('Plan to existing TO succeeded', { toId: prePlannedData.transportOrder?.id });

        const allPlanningBlocks = [
          ...prePlannedData.planningBlocks.depotInPlanningBlocks,
          ...prePlannedData.planningBlocks.depotOutPlanningBlocks
        ];

        // Update display
        this.sharedActions.updatePlanningBlocksDisplayCpl(prePlannedData.planningBlocks.depotInPlanningBlocks);
        this.sharedActions.updatePlanningBlocksDisplayCpl(prePlannedData.planningBlocks.depotOutPlanningBlocks);

        // Update PBs and continue with group/send
        return this.updatePlanningBlocksIfNeeded(allPlanningBlocks, input).pipe(
          switchMap(() => {
            if (options.groupAfterPlan) {
              return this.groupAndSend(prePlannedData, options.sendAfterGroup);
            }
            return of({
              success: true,
              transportOrderId: prePlannedData.transportOrder?.id,
              transportOrder: prePlannedData.transportOrder,
              planningBlocks: allPlanningBlocks,
              planUnit: prePlannedData.planningBlocks
            });
          })
        );
      }),
      catchError((error) => {
        this.devLogger.error('PlanningOrchestration', 'Plan to existing TO failed', error);
        return of({
          success: false,
          error: this.extractErrorMessage(error)
        });
      })
    );
  }

  /**
   * Group and optionally send planning blocks
   */
  private groupAndSend(
    prePlannedData: TransportOrderWithPlanUnit,
    shouldSend: boolean = true
  ): Observable<PlanningResult> {
    this.devLogger.planning('Grouping planning blocks');

    return this.sharedService.group(prePlannedData.planningBlocks, '').pipe(
      switchMap((groupedData: TransportOrderPlanUnit) => {
        this.devLogger.planning('Grouping succeeded');

        if (shouldSend) {
          return this.sendPlanningBlocks(groupedData, prePlannedData.transportOrder?.id, prePlannedData.transportOrder);
        }

        return of({
          success: true,
          transportOrderId: prePlannedData.transportOrder?.id,
          transportOrder: prePlannedData.transportOrder,
          planUnit: groupedData
        });
      }),
      catchError((error) => {
        this.devLogger.error('PlanningOrchestration', 'Grouping failed', error);
        return of({
          success: false,
          error: this.extractErrorMessage(error)
        });
      })
    );
  }

  /**
   * Send (finalize) planning blocks
   */
  private sendPlanningBlocks(
    groupedData: TransportOrderPlanUnit,
    transportOrderId: string,
    transportOrder?: any
  ): Observable<PlanningResult> {
    this.devLogger.planning('Sending planning blocks', { toId: transportOrderId });

    return this.sharedService.send(groupedData, '').pipe(
      map((planUnit: TransportOrderPlanUnit) => {
        this.devLogger.planning('Send succeeded', { toId: transportOrderId });

        // Update display with final data
        this.sharedActions.updatePlanningBlocksDisplayCpl(planUnit.depotInPlanningBlocks);
        this.sharedActions.updatePlanningBlocksDisplayCpl(planUnit.depotOutPlanningBlocks);

        return {
          success: true,
          transportOrderId,
          transportOrder,
          planUnit,
          planningBlocks: [...planUnit.depotInPlanningBlocks, ...planUnit.depotOutPlanningBlocks]
        };
      }),
      catchError((error) => {
        this.devLogger.error('PlanningOrchestration', 'Send failed', error);
        return of({
          success: false,
          error: this.extractErrorMessage(error)
        });
      })
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VALIDATION METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Validate planning dates for all blocks
   */
  public validatePlanningDates(input: PlanningInput): DateValidationResult {
    const errors: string[] = [];
    const invalidBlocks: PlanningBlock[] = [];
    const planDate = moment(input.planDate);

    // Check depot-out blocks
    for (const pb of input.depotOutPlanningBlocks) {
      if (!this.isDepotOutDateValid(pb, planDate)) {
        errors.push(`Depot Out From date is not compatible with selected plan date for PB ${pb.orderNumber}`);
        invalidBlocks.push(pb);
      }
    }

    // Check depot-in blocks
    for (const pb of input.depotInPlanningBlocks) {
      if (!this.isDepotInDateValid(pb, planDate)) {
        errors.push(`Depot In From date is not compatible with selected plan date for PB ${pb.orderNumber}`);
        invalidBlocks.push(pb);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      invalidBlocks
    };
  }

  /**
   * Check if depot-out date is valid for planning
   */
  private isDepotOutDateValid(pb: PlanningBlock, planDate: moment.Moment): boolean {
    if (!pb.depotOutFrom) return true;

    const depotOutFrom = moment(pb.depotOutFrom);
    if (!depotOutFrom.isValid()) return true;

    return planDate.isSameOrAfter(depotOutFrom.startOf('day'));
  }

  /**
   * Check if depot-in date is valid for planning
   */
  private isDepotInDateValid(pb: PlanningBlock, planDate: moment.Moment): boolean {
    if (!pb.depotInFrom) return true;

    const depotInFrom = moment(pb.depotInFrom);
    if (!depotInFrom.isValid()) return true;

    return planDate.isSameOrAfter(depotInFrom.startOf('day'));
  }

  /**
   * Check driver absence for a given date
   */
  public checkDriverAbsence(
    planDate: Date,
    driverId: string
  ): Observable<{ isAbsent: boolean; absenceDetails?: any }> {
    const formattedDate = moment(planDate).startOf('day').format('YYYY-MM-DD');

    return this.sharedService.checkDriverAbsence(formattedDate, driverId).pipe(
      map((absenceData: any[]) => ({
        isAbsent: absenceData && absenceData.length > 0,
        absenceDetails: absenceData?.[0]
      })),
      catchError(() => of({ isAbsent: false }))
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPER METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Update planning blocks if status update is enabled
   */
  private updatePlanningBlocksIfNeeded(
    planningBlocks: PlanningBlock[],
    input: PlanningInput
  ): Observable<boolean> {
    if (!input.statusUpdate?.enabled) {
      return of(false);
    }

    const updateModels: UpdateContainerStatusModel[] = planningBlocks
      .filter(pb => input.statusUpdate.status !== pb.containerStatus)
      .map(pb => ({
        planningBlockId: pb.id,
        status: input.statusUpdate.status || pb.containerStatus,
        addressLocation: {
          addressId: input.statusUpdate.location?.addressId || '',
          locationCode: input.statusUpdate.location?.locationCode || '',
          location: ''
        },
        userId: input.userId
      }));

    if (updateModels.length === 0) {
      return of(false);
    }

    this.devLogger.planning('Updating planning blocks status', { count: updateModels.length });

    return this.sharedService.updateBulkPlanningBlocksStatus(updateModels).pipe(
      map((returnedPbs) => {
        this.sharedActions.updatePlanningBlocksDisplayCpl(returnedPbs);
        return true;
      }),
      catchError((error) => {
        this.devLogger.error('PlanningOrchestration', 'Status update failed', error);
        return of(false);
      })
    );
  }

  /**
   * Extract error message from various error formats
   */
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.feedback?.[0]?.description) return error.feedback[0].description;
    if (error?.error?.message) return error.error.message;
    return 'An unexpected error occurred';
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ROLLBACK METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Undo a pre-plan operation
   */
  public undoPreplan(planUnit: TransportOrderPlanUnit, userId: string): void {
    this.devLogger.planning('Undoing pre-plan');
    this.sharedService.undoPreplan(planUnit, userId);
  }

  /**
   * Undo a send operation
   */
  public undoSend(planUnit: TransportOrderPlanUnit, userId: string): Observable<TransportOrderPlanUnit> {
    this.devLogger.planning('Undoing send');
    return this.sharedService.undoSend(planUnit, userId);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // OPTIMISTIC UI METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  // Subjects for optimistic state changes (consumed by Redux/facade)
  private optimisticTOCreated$ = new Subject<OptimisticTransportOrder>();
  private optimisticPBsCreated$ = new Subject<OptimisticPlanningBlock[]>();
  private optimisticConfirmed$ = new Subject<PlanningConfirmedPayload>();
  private optimisticFailed$ = new Subject<PlanningFailedPayload>();

  /** Observable for when optimistic TOs are created */
  public get onOptimisticTOCreated$(): Observable<OptimisticTransportOrder> {
    return this.optimisticTOCreated$.asObservable();
  }

  /** Observable for when optimistic PBs are created */
  public get onOptimisticPBsCreated$(): Observable<OptimisticPlanningBlock[]> {
    return this.optimisticPBsCreated$.asObservable();
  }

  /** Observable for when optimistic planning is confirmed */
  public get onOptimisticConfirmed$(): Observable<PlanningConfirmedPayload> {
    return this.optimisticConfirmed$.asObservable();
  }

  /** Observable for when optimistic planning fails */
  public get onOptimisticFailed$(): Observable<PlanningFailedPayload> {
    return this.optimisticFailed$.asObservable();
  }

  /**
   * Execute planning with optimistic UI - INSTANT RESPONSE
   * 
   * This method:
   * 1. Validates input quickly (< 50ms)
   * 2. Creates optimistic TO/PBs immediately
   * 3. Emits optimistic state to store
   * 4. Fires async request to backend
   * 5. Returns immediately with optimistic response
   * 
   * The actual BC processing happens asynchronously. Confirmation/failure
   * is handled via SignalR events that update the optimistic state.
   */
  public executePlanningOptimistic(
    input: PlanningInput
  ): OptimisticPlanResponse {
    const correlationId = generateCorrelationId();
    const optimisticTOId = generateOptimisticId('TO');
    const now = new Date();

    this.devLogger.planning('Optimistic planning started', {
      correlationId,
      optimisticTOId,
      depotIn: input.depotInPlanningBlocks.length,
      depotOut: input.depotOutPlanningBlocks.length
    });

    // Step 1: Quick validation (synchronous, must be fast)
    const dateValidation = this.validatePlanningDates(input);
    if (!dateValidation.isValid) {
      this.devLogger.planning('Optimistic validation failed', dateValidation.errors);
      return {
        correlationId,
        optimisticTOId,
        accepted: false,
        validationErrors: dateValidation.errors
      };
    }

    // Step 2: Create optimistic Transport Order
    const optimisticTO = this.createOptimisticTransportOrder(
      input,
      correlationId,
      optimisticTOId,
      now
    );

    // Step 3: Create optimistic Planning Blocks
    const optimisticPBs = this.createOptimisticPlanningBlocks(
      input,
      correlationId,
      optimisticTOId,
      now
    );

    // Step 4: Dispatch to Redux store for immediate UI update
    this.store.dispatch({
      type: ActionKeys.OPTIMISTIC_CREATE_TO,
      payload: optimisticTO
    });
    this.store.dispatch({
      type: ActionKeys.OPTIMISTIC_CREATE_PB,
      payload: optimisticPBs
    });
    
    // Also emit for any observers (backwards compatibility)
    this.optimisticTOCreated$.next(optimisticTO);
    this.optimisticPBsCreated$.next(optimisticPBs);
    
    this.devLogger.planning('Optimistic state dispatched to Redux', {
      correlationId,
      optimisticTOId,
      truckId: optimisticTO.truck?.id
    });

    // Step 5: Fire async request (fire-and-forget pattern)
    this.executeBackendPlanningAsync(input, correlationId, optimisticTOId);

    // Step 6: Return immediately with optimistic response
    this.devLogger.planning('Optimistic response returned instantly', {
      correlationId,
      optimisticTOId,
      accepted: true
    });

    return {
      correlationId,
      optimisticTOId,
      accepted: true,
      estimatedCompletion: new Date(now.getTime() + 10000) // ~10 seconds
    };
  }

  /**
   * Create an optimistic Transport Order from input
   */
  private createOptimisticTransportOrder(
    input: PlanningInput,
    correlationId: string,
    optimisticTOId: string,
    timestamp: Date
  ): OptimisticTransportOrder {
    const allPBs = [...input.depotInPlanningBlocks, ...input.depotOutPlanningBlocks];
    const firstPB = allPBs[0];

    // Build optimistic TO with available client-side data
    // Note: using 'as any' for type compatibility between different PlanningBlock/TransportOrder interfaces
    const optimisticTO: OptimisticTransportOrder = {
      // TransportOrder fields (using available data)
      id: optimisticTOId,
      startAddress: firstPB?.firstPlanAddressSearchName || '',
      endAddress: firstPB?.lastPlanAddressSearchName || '',
      truck: (input.capacitySlot?.truck || { id: 0, name: '' }) as any,
      haulier: (firstPB?.haulier || { id: 0, name: '' }) as any,
      date: input.planDate,
      driver: input.selectedDriver?.name || input.capacitySlot?.driver?.name || '',
      operationalStatus: 'Planning...',
      financialStatus: '',
      planningGroup: firstPB?.planningGroup || '',
      remarks: '',
      revenue: 0, // Will be calculated by BC
      cost: 0,    // Will be calculated by BC
      margin: 0,  // Will be calculated by BC
      marginPercentage: 0,
      planningBlocks: allPBs as any,
      isHidden: false,

      // Optimistic metadata
      optimisticState: 'pending',
      optimisticId: optimisticTOId,
      pendingSince: timestamp,
      correlationId: correlationId,
      initiatedByUserId: input.userId,
      pendingOperations: 1
    };

    return optimisticTO;
  }

  /**
   * Create optimistic Planning Blocks from input
   */
  private createOptimisticPlanningBlocks(
    input: PlanningInput,
    correlationId: string,
    optimisticTOId: string,
    timestamp: Date
  ): OptimisticPlanningBlock[] {
    const allPBs = [...input.depotInPlanningBlocks, ...input.depotOutPlanningBlocks];

    return allPBs.map((pb, index) => {
      // Note: using spread + type assertion for compatibility between PlanningBlock interfaces
      const optimisticPB: OptimisticPlanningBlock = {
        ...(pb as any),
        // Update fields to show optimistic state
        transportOrderNumber: optimisticTOId,
        operationalStatus: 'Planning...',
        driverFullName: input.selectedDriver?.name || input.capacitySlot?.driver?.name || pb.driverFullName,
        truck: input.capacitySlot?.truck?.name || pb.truck,

        // Optimistic metadata
        optimisticState: 'pending',
        optimisticId: generateOptimisticId('PB'),
        pendingSince: timestamp,
        correlationId: correlationId,
        initiatedByUserId: input.userId,
        serverPbId: pb.id // Keep original ID for reference
      };

      return optimisticPB;
    });
  }

  /**
   * Execute the actual backend planning asynchronously
   * This runs in the background after the optimistic response is returned
   */
  private executeBackendPlanningAsync(
    input: PlanningInput,
    correlationId: string,
    optimisticTOId: string
  ): void {
    // Execute the real planning in the background
    this.executePlanning(input, {
      groupAfterPlan: true,
      sendAfterGroup: true
    }).subscribe(
      (result: PlanningResult) => {
        if (result.success) {
          // Planning succeeded - emit confirmation
          // Note: using 'as any' for type compatibility between PlanningBlock interfaces
          const confirmPayload: PlanningConfirmedPayload = {
            correlationId,
            transportOrder: result.transportOrder as any,
            planningBlocks: (result.planningBlocks || []) as any,
            calculatedValues: {
              // BC would provide these
              distances: [],
              costs: { total: result.transportOrder?.cost || 0, breakdown: {} },
              etas: [],
              conflicts: []
            }
          };

          this.devLogger.planning('Backend confirmed optimistic planning', {
            correlationId,
            actualTOId: result.transportOrderId
          });

          this.optimisticConfirmed$.next(confirmPayload);
        } else {
          // Planning failed - emit failure
          const failPayload: PlanningFailedPayload = {
            correlationId,
            optimisticTOId,
            optimisticPbIds: [],
            errorCode: 'PLANNING_FAILED',
            errorMessage: result.error || 'Planning failed',
            canRetry: true
          };

          this.devLogger.planning('Backend rejected optimistic planning', {
            correlationId,
            error: result.error
          });

          this.optimisticFailed$.next(failPayload);
        }
      },
      (error) => {
        // Network or other error
        const failPayload: PlanningFailedPayload = {
          correlationId,
          optimisticTOId,
          optimisticPbIds: [],
          errorCode: 'NETWORK_ERROR',
          errorMessage: this.extractErrorMessage(error),
          canRetry: true
        };

        this.devLogger.error('PlanningOrchestration', 'Backend planning error', error);
        this.optimisticFailed$.next(failPayload);
      }
    );
  }

  /**
   * Confirm an optimistic operation (called when SignalR confirms from BC)
   */
  public confirmOptimistic(payload: PlanningConfirmedPayload): void {
    this.devLogger.planning('Confirming optimistic operation', {
      correlationId: payload.correlationId,
      actualTOId: payload.transportOrder?.id
    });
    this.optimisticConfirmed$.next(payload);
  }

  /**
   * Fail an optimistic operation (called when SignalR reports failure from BC)
   */
  public failOptimistic(payload: PlanningFailedPayload): void {
    this.devLogger.planning('Failing optimistic operation', {
      correlationId: payload.correlationId,
      error: payload.errorMessage
    });
    this.optimisticFailed$.next(payload);
  }

  /**
   * Create a PlanningStartedPayload for SignalR broadcast
   */
  public createPlanningStartedPayload(
    input: PlanningInput,
    correlationId: string,
    optimisticTOId: string,
    optimisticPbIds: string[]
  ): PlanningStartedPayload {
    const firstPB = input.depotInPlanningBlocks[0] || input.depotOutPlanningBlocks[0];

    return {
      correlationId,
      optimisticTOId,
      optimisticPbIds,
      capacitySlotId: input.capacitySlot?.id || 0,
      plannedTimeFrom: input.planDate,
      plannedTimeUntil: input.planDate,
      initiatedByUserId: input.userId,
      planningGroup: firstPB?.planningGroup || ''
    };
  }
}
